import React, { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import AddLocation from "./AddLocation";

function LocationList() {
  const [locations, setLocations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  // ✅ Fetch Tenants
  const fetchTenants = async () => {
    try {
      const res = await axiosClient.get("/tenant/api/list");
      setTenants(res.data.data || []);
    } catch (err) {
      console.error("Error fetching tenants:", err);
      alert("Unable to load tenants");
    }
  };

  // ✅ Fetch Locations (optional tenant filter)
  const fetchLocations = async () => {
    try {
      const url = selectedTenant
        ? `/tenant/api/locations/list?tenant_id=${selectedTenant}`
        : `/tenant/api/locations/list`;
      const res = await axiosClient.get(url);
      setLocations(res.data.data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
      alert("Unable to load locations");
    }
  };

  // ✅ On mount
  useEffect(() => {
    fetchTenants();
  }, []);

  // ✅ When tenant selection changes
  useEffect(() => {
    fetchLocations();
  }, [selectedTenant]);

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold text-primary">🏢 Locations</h4>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
          style={{
            background: "linear-gradient(45deg, #007bff, #0056b3)",
            border: "none",
          }}
        >
          ➕ Add Location
        </button>
      </div>

      {/* Tenant Filter */}
      <div className="mb-3">
        <label className="form-label fw-semibold">Select Tenant:</label>
        <select
          className="form-select"
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
        >
          <option value="">All Tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-primary">
            <tr>
              <th>Tenant</th>
              <th>Name</th>
              <th>Code</th>
              <th>City</th>
              <th>State</th>
              <th>Status</th>
              <th style={{ width: "220px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted py-3">
                  No locations found
                </td>
              </tr>
            ) : (
              locations.map((l) => (
                <tr key={l.id}>
                  <td>{tenants.find((t) => t.id === l.tenant_id)?.name || "—"}</td>
                  <td>{l.name}</td>
                  <td>{l.code}</td>
                  <td>{l.city}</td>
                  <td>{l.state}</td>
                  <td>
                    <span
                      className={`badge ${
                        l.status === "Active" ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {l.status}
                    </span>
                  </td>
                  <td className="d-flex gap-2">
                    {/* ✏️ Edit */}
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setEditingLocation(l)}
                    >
                      ✏️ Edit
                    </button>

                    {/* 🔁 Activate/Deactivate */}
                    <button
                      className={`btn btn-sm ${
                        l.status === "Active" ? "btn-warning" : "btn-success"
                      }`}
                      onClick={async () => {
                        try {
                          await axiosClient.put(
                            `/tenant/api/locations/${l.id}/toggle`
                          );
                          fetchLocations();
                        } catch {
                          alert("Failed to change status");
                        }
                      }}
                    >
                      {l.status === "Active" ? "Deactivate" : "Activate"}
                    </button>

                    {/* 🗑 Delete */}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete location "${l.name}"?`
                          )
                        ) {
                          try {
                            await axiosClient.delete(
                              `/tenant/api/locations/${l.id}`
                            );
                            fetchLocations();
                          } catch {
                            alert("Failed to delete location");
                          }
                        }
                      }}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddLocation
          tenants={tenants}
          onClose={() => {
            setShowAddModal(false);
            fetchLocations();
          }}
        />
      )}

      {/* Edit Modal */}
      {editingLocation && (
        <AddLocation
          tenants={tenants}
          existing={editingLocation}
          onClose={() => {
            setEditingLocation(null);
            fetchLocations();
          }}
        />
      )}
    </div>
  );
}

export default LocationList;
